import * as ChapterDomain from "./chapter-domain"
import * as Model from "../../model"

/**
 * Contient la liste des numéros de comptes de charges.
 */
export const EXPENSES_ACCOUNT_NUMBERS = ["675", "676", "68"]
/**
 * Contient la liste des
 * numéros de comptes de produits.
 */
export const REVENUES_ACCOUNT_NUMBERS = ["775", "776", "777", "78"]

/**
 * Vérifie si le compte passé en paramètre est enregistré dans le chapitre passé
 * en paramètre en fonction de la liste de lacas.
 * @param account
 * @param chapter
 * @param lacas
 */
export function accountIsInChapter(
	account: Model.Account,
	chapter: Model.Chapter,
	lacas: Model.LocalAuthorityChapterAccount[]
): boolean {
	const accountChapter = getAttachedChapter(account, lacas)
	return ChapterDomain.equal(accountChapter, chapter)
}

/**
 * Compare les deux comptes par leur numéro.
 * @param account1
 * @param account2
 * @returns Si les deux comptes sont indéfinis, retourne 0.
 * Si le premier compte est défini mais pas le second, retourne 1.
 * Si le premier compte n'est pas défini mais le second oui, retourne -1.
 * Si les numéros des deux comptes sont égaux, retourne 0.
 * Si le numéro du premier est supérieur au numéro du second (ordre
 * alphabétique), retourne 1, sinon retourne -1.
 */
export function compareByNumber(
	account1: Model.Account,
	account2: Model.Account
): number {
	if (!account1 && !account2) return 0
	if (account1 && !account2) return 1
	if (!account1 && account2) return -1
	if (account1.number === account2.number) return 0
	return account1.number > account2.number ? 1 : -1
}

/**
 * Vérifie si la liste des comptes passée en paramètre contient le compte passé
 * en paramètre.
 * @param accounts
 * @param account
 */
export function contains(
	accounts: Model.Account[],
	account: Model.Account
): boolean {
	return accounts.some(a => equal(a, account))
}

/**
 * Retourne la liste distinguée des comptes attachés aux constats passés en
 * paramètre.
 * @param observations
 */
export function distinctAccounts(
	observations: Model.Observation[]
): Model.Account[] {
	return observations.reduce<Model.Account[]>((accounts, o) => {
		if (!contains(accounts, o.account)) accounts.push(o.account)
		return accounts
	}, [])
}

/**
 * Vérifie si les comptes passés en paramètres sont les mêmes.
 * @param account1
 * @param account2
 */
export function equal(
	account1: Model.Account,
	account2: Model.Account
): boolean {
	return !(account1 || account2) || account1 && account2 && account1.id === account2.id
}

/**
 * Liste les comptes utilisés pour le calcul de la capacité d'autofinancement
 * brute, soit les comptes de charges et les comptes de produit sans
 * distinction.
 * @param lacas
 */
export function findGrossCashFlowAccounts(
	lacas: Model.LocalAuthorityChapterAccount[]
): Model.Account[] {
	return lacas.reduce((accounts, laca) => {
		if (!(laca && laca.account && laca.account.operation)) return accounts
		if (isExpenseAccount(laca.account) || isRevenueAccount(laca.account))
			return accounts

		accounts.push(laca.account)

		return accounts
	}, [])
}

/**
 * Liste les comptes de charge.
 * @param lacas
 * @see isExpenseAccount
 */
export function findExpenseAccounts(
	lacas: Model.LocalAuthorityChapterAccount[]
): Model.Account[] {
	return lacas.reduce((accounts, laca) => {
		if (laca && laca.account && isExpenseAccount(laca.account))
			accounts.push(laca.account)
		return accounts
	}, [])
}

/**
 * Liste les comptes de produit.
 * @param lacas
 * @see isRevenueAccount
 */
export function findRevenueAccounts(
	lacas: Model.LocalAuthorityChapterAccount[]
): Model.Account[] {
	return lacas.reduce((accounts, laca) => {
		if (laca && laca.account && isRevenueAccount(laca.account))
			accounts.push(laca.account)
		return accounts
	}, [])
}

/**
 * Retourne l'index du compte passé en paramètre parmi la liste passée en
 * paramètre.
 * @param accounts
 * @param account
 */
export function findIndexById(
	accounts: Model.Account[],
	account: Model.Account
): number | null {
	const index = accounts.findIndex(a => a.id === account.id)
	return index !== -1 ? index : null
}

/**
 * Retourne le chapitre dans lequel le compte est catégorisé en fonction de la
 * liste passée en paramètre.
 * @param account
 * @param lacas
 */
export function getAttachedChapter(
	account: Model.Account,
	lacas  : Model.LocalAuthorityChapterAccount[]
): Model.Chapter {
	const laca = lacas.find(laca => laca.account.id === account.id)
	return laca ? laca.chapter : null
}

/**
 * Vérifie si le compte est un compte de charge.
 * @param account
 * @see EXPENSES_ACCOUNT_NUMBERS
 */
export function isExpenseAccount(account: Model.Account): boolean {
	return EXPENSES_ACCOUNT_NUMBERS.some(n => account.number.startsWith(n))
}

/**
 * Vérifie si un compte est utilisé pour le calcul de la capacité
 * d'autofinancement brute, soit un compte de fonctionnement qui ne soit ni de
 * charge, ni de produit.
 * @param account
 * @see isExpenseAccount
 * @see isRevenueAccount
 */
export function isGrossCashFlowAccount(account: Model.Account): boolean {
	return account.operation
	&& !isExpenseAccount(account)
	&& !isRevenueAccount(account)
}

/**
 * Vérifie si le compte passé en paramètre est un remboursement d'emprunt.
 *
 * Un compte est un remboursement d'emprunt s'il est en dépense d'investissement
 * uniquement et si son numéro commence par 164.
 *
 * @param account
 */
export function isLoanReimbursement(account: Model.Account): boolean {
	if (!account || account.operation || account.receipts || !account.expense)
		return false

	return account.number.startsWith("164")
}

/**
 * Vérifie si le compte est un compte de produit.
 * @param account
 * @see REVENUES_ACCOUNT_NUMBERS
 */
export function isRevenueAccount(account: Model.Account): boolean {
	return REVENUES_ACCOUNT_NUMBERS.some(n => account.number.startsWith(n))
}

/**
 * Trie les comptes par ordre de numérotation.
 * @param accounts
 * @param asc Si `true` (par défaut), trie par ordre croissant, sinon trie par
 * ordre décroissant.
 */
export function sortByNumber(
	accounts: Model.Account[],
	asc = true
): Model.Account[] {
	return accounts.sort((a1, a2) => a1.number > a2.number && asc ? 1 : -1)
}

/**
 * Retourne une chaine sous la forme "<number> - <label>" si le compte existe,
 * sinon "Indéterminé".
 * @param account
 */
export function titlize(account: Model.Account): string {
	return account ? `${account.number} - ${account.label}` : "Indéterminé"
}

export function getAccount(
	accounts: Account[],
	account: Account
): Account | null {
	return accounts.find(acc => acc.id === account.id) || null
}



// WEBPACK FOOTER //
// ./src/domain/sfp/account-domain.ts